import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { BRAND } from '../../theme';

interface Shortcut {
  keys: string;
  description: string;
  /** Shown in the always-visible strip, not only in the dialog */
  inline?: boolean;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
const mod = isMac ? '⌘' : 'Ctrl';

export const SHORTCUTS: Shortcut[] = [
  { keys: 'Tab', description: 'Next field', inline: true },
  { keys: 'Shift + Tab', description: 'Previous field', inline: true },
  { keys: `${mod} + S`, description: 'Save draft', inline: true },
  { keys: `${mod} + Enter`, description: 'Add entry' },
  { keys: 'Alt + →', description: 'Next PDF page' },
  { keys: 'Alt + ←', description: 'Previous PDF page' },
  { keys: 'Esc', description: 'Cancel editing an entry' }
];

const KeyCap: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-block',
      px: 0.75,
      py: 0.15,
      borderRadius: 0.75,
      border: `1px solid ${BRAND.border}`,
      backgroundColor: '#F8FAFD',
      fontSize: 11,
      fontWeight: 700,
      color: BRAND.textMuted,
      whiteSpace: 'nowrap'
    }}
  >
    {children}
  </Box>
);

const ShortcutsBar: React.FC = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.25,
          px: 2,
          py: 1.25,
          borderTop: `1px solid ${BRAND.border}`,
          backgroundColor: '#FBFCFE'
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          Keyboard shortcuts:
        </Typography>
        {SHORTCUTS.filter(shortcut => shortcut.inline).map(shortcut => (
          <Box key={shortcut.keys} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <KeyCap>{shortcut.keys}</KeyCap>
            <Typography variant="caption" color="text.secondary">
              {shortcut.description}
            </Typography>
          </Box>
        ))}
        <Link
          component="button"
          type="button"
          variant="caption"
          onClick={() => setOpen(true)}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
        >
          View all shortcuts
          <OpenInNewIcon sx={{ fontSize: 12 }} />
        </Link>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Keyboard shortcuts</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableBody>
              {SHORTCUTS.map(shortcut => (
                <TableRow key={shortcut.keys}>
                  <TableCell sx={{ width: 120, borderBottom: 'none' }}>
                    <KeyCap>{shortcut.keys}</KeyCap>
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>{shortcut.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShortcutsBar;
